import React, { useState, useEffect, useRef } from 'react';
import { Database, Search, UploadCloud, Loader2, Users, ChevronLeft, ChevronRight, Plus, X, Trash2 } from 'lucide-react';
import api from '../services/api';

// ⭐ CORRIGIDO: categorias (setores) agora são livres — o usuário cria/exclui
// à vontade em vez de uma lista fixa no código. "Geral" continua sendo uma
// aba especial (visão de tudo), não uma categoria de verdade.
function BaseDados() {
  const [categories, setCategories] = useState([]);
  const [activeSector, setActiveSector] = useState('geral');
  const [lists, setLists] = useState([]);
  const [activeList, setActiveList] = useState('');
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const fileInputRef = useRef(null);
  const LIMIT = 50;

  const activeCategory = categories.find(c => c.slug === activeSector);
  const activeLabel = activeSector === 'geral' ? 'Geral' : (activeCategory?.label || activeSector);

  const loadCategories = () => {
    api.get('/api/database/categories')
      .then(res => setCategories(res.data))
      .catch(err => console.error('Erro ao buscar categorias:', err));
  };

  useEffect(() => { loadCategories(); }, []);

  // Busca as listas (abas) disponíveis quando troca de categoria
  useEffect(() => {
    setActiveList('');
    setPage(1);
    if (activeSector === 'geral') {
      setLists([]);
      return;
    }
    api.get('/api/database/lists', { params: { sector: activeSector } })
      .then(res => setLists(res.data))
      .catch(err => console.error('Erro ao buscar listas:', err));
  }, [activeSector]);

  // Busca os registros sempre que categoria, lista, busca ou página mudam
  useEffect(() => {
    setLoading(true);
    const params = { sector: activeSector, page, limit: LIMIT };
    if (activeList) params.list_name = activeList;
    if (search) params.search = search;

    const timer = setTimeout(() => {
      api.get('/api/database', { params })
        .then(res => {
          setRecords(res.data.records);
          setTotal(res.data.total);
        })
        .catch(err => console.error('Erro ao buscar base de dados:', err))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [activeSector, activeList, search, page]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (activeSector === 'geral') {
      alert('Escolha uma categoria específica (ou crie uma nova) antes de subir a planilha.');
      return;
    }
    if (!window.confirm(`Isso vai SUBSTITUIR todos os dados atuais de ${activeLabel}. Continuar?`)) {
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sector', activeSector);
      const res = await api.post('/api/database/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(`✅ ${res.data.total} registros importados em ${res.data.sheets.length} lista(s).`);
      const listsRes = await api.get('/api/database/lists', { params: { sector: activeSector } });
      setLists(listsRes.data);
      setActiveList('');
      setPage(1);
    } catch (err) {
      alert('Erro ao importar: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryLabel.trim()) return;
    setSavingCategory(true);
    try {
      const res = await api.post('/api/database/categories', { label: newCategoryLabel.trim() });
      setCategories(prev => [...prev, res.data].sort((a, b) => a.label.localeCompare(b.label)));
      setActiveSector(res.data.slug);
      setShowNewCategory(false);
      setNewCategoryLabel('');
    } catch (err) {
      alert('Erro ao criar categoria: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (e, slug, label) => {
    e.stopPropagation();
    if (!window.confirm(`Excluir a categoria "${label}"? Isso apaga TODOS os registros dela também.`)) return;
    try {
      await api.delete(`/api/database/categories/${slug}`);
      setCategories(prev => prev.filter(c => c.slug !== slug));
      if (activeSector === slug) setActiveSector('geral');
    } catch (err) {
      alert('Erro ao excluir categoria: ' + (err.response?.data?.error || err.message));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database size={24} className="text-green-600" />
            Base de Dados
          </h1>
          <p className="text-gray-500 text-sm mt-1">Planilhas de contatos por categoria, organizadas por lista</p>
        </div>
        {activeSector !== 'geral' && (
          <div>
            <input type="file" ref={fileInputRef} onChange={handleUpload} accept=".xlsx,.xls,.csv" className="hidden" />
            <button
              onClick={() => fileInputRef.current.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold shadow-sm disabled:opacity-50"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
              {uploading ? 'Importando...' : `Subir planilha de ${activeLabel}`}
            </button>
          </div>
        )}
      </div>

      {/* Abas de categoria */}
      <div className="flex items-center gap-2 border-b border-gray-100 flex-wrap">
        <button
          onClick={() => setActiveSector('geral')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeSector === 'geral' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Geral
        </button>
        {categories.map(c => (
          <button
            key={c.slug}
            onClick={() => setActiveSector(c.slug)}
            className={`group flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeSector === c.slug ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {c.label}
            <span
              onClick={(e) => handleDeleteCategory(e, c.slug, c.label)}
              className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-opacity"
              title="Excluir categoria"
            >
              <Trash2 size={13} />
            </span>
          </button>
        ))}
        <button
          onClick={() => setShowNewCategory(true)}
          className="flex items-center gap-1 px-3 py-2.5 text-sm font-medium text-green-600 hover:text-green-700"
        >
          <Plus size={16} /> Nova Categoria
        </button>
      </div>

      {/* Modal de nova categoria */}
      {showNewCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Nova Categoria</h2>
              <button onClick={() => setShowNewCategory(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Categoria</label>
                <input
                  type="text"
                  autoFocus
                  placeholder="Ex: Cardiologia, Fisioterapia..."
                  value={newCategoryLabel}
                  onChange={e => setNewCategoryLabel(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowNewCategory(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" disabled={savingCategory} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                  {savingCategory ? 'Criando...' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          {activeSector !== 'geral' && (
            <select
              value={activeList}
              onChange={e => { setActiveList(e.target.value); setPage(1); }}
              className="p-2.5 border border-gray-200 rounded-xl text-sm min-w-[220px] focus:ring-2 focus:ring-green-500 outline-none"
            >
              <option value="">Todas as listas</option>
              {lists.map(l => (
                <option key={l.list_name} value={l.list_name}>{l.list_name} ({l.total})</option>
              ))}
            </select>
          )}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por nome ou telefone..."
              className="w-full pl-9 p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <Loader2 size={28} className="animate-spin mx-auto mb-2" />
            Carregando...
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-12">
            <Users size={40} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-400 text-sm">
              {activeSector === 'geral' ? 'Nenhum registro na base de dados ainda' : `Nenhum registro em ${activeLabel} ainda — suba uma planilha pra começar`}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-gray-500">
                    <th className="pb-2 font-medium">Nome</th>
                    <th className="pb-2 font-medium">Telefone</th>
                    <th className="pb-2 font-medium">Telefone 2</th>
                    {activeSector === 'geral' && <th className="pb-2 font-medium">Categoria</th>}
                    <th className="pb-2 font-medium">Lista</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2.5 text-gray-800">{r.nome || '-'}</td>
                      <td className="py-2.5 text-gray-600">{r.telefone || '-'}</td>
                      <td className="py-2.5 text-gray-600">{r.telefone2 || '-'}</td>
                      {activeSector === 'geral' && (
                        <td className="py-2.5">
                          <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium capitalize">{r.sector}</span>
                        </td>
                      )}
                      <td className="py-2.5">
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{r.list_name}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <span className="text-xs text-gray-400">{total} registro(s) no total</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                  <ChevronLeft size={16} />
                </button>
                <span className="text-xs text-gray-500">Página {page} de {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BaseDados;
